import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context';
import styles from './LoginPage.module.css';

// ---------------------------------------------------------------------------
// Eye icon SVGs — open and closed variants
// ---------------------------------------------------------------------------

function EyeOpenIcon(): JSX.Element {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function EyeClosedIcon(): JSX.Element {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
      <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// MangoGrid power-grid icon — hexagonal with internal lines (amber)
// ---------------------------------------------------------------------------

function MangoGridIcon({ size = 48 }: { size?: number }): JSX.Element {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 48 48"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      {/* Outer hexagon */}
      <polygon
        points="24,4 42,14 42,34 24,44 6,34 6,14"
        fill="none"
        stroke="#f59e0b"
        strokeWidth="2.5"
        strokeLinejoin="round"
      />
      {/* Internal grid lines */}
      <line x1="24" y1="4" x2="24" y2="44" stroke="#f59e0b" strokeWidth="1.5" strokeOpacity="0.55" />
      <line x1="6" y1="14" x2="42" y2="34" stroke="#f59e0b" strokeWidth="1.5" strokeOpacity="0.55" />
      <line x1="42" y1="14" x2="6" y2="34" stroke="#f59e0b" strokeWidth="1.5" strokeOpacity="0.55" />
      {/* Power bolt */}
      <polygon
        points="26,16 20,26 24,26 22,34 30,22 26,22"
        fill="#f59e0b"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Left panel background SVG — abstract transmission network art
// ---------------------------------------------------------------------------

function GridArtSVG(): JSX.Element {
  return (
    <svg
      className={styles.leftPanelArt}
      viewBox="0 0 900 700"
      preserveAspectRatio="xMidYMid slice"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <defs>
        {/* Warm sky gradient — cool blue sky to golden ground */}
        <linearGradient id="lgSkyGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%"   stopColor="#b8d0e4" />
          <stop offset="42%"  stopColor="#d4b86a" />
          <stop offset="100%" stopColor="#a87830" />
        </linearGradient>
        {/* Bar chart gradient — emerald top to cyan bottom */}
        <linearGradient id="lgBarGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%"   stopColor="#34d399" />
          <stop offset="100%" stopColor="#0891b2" />
        </linearGradient>
        {/* Ground haze gradient */}
        <linearGradient id="lgHaze" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%"   stopColor="rgba(180,150,80,0)"  />
          <stop offset="100%" stopColor="rgba(120,90,30,0.45)" />
        </linearGradient>
      </defs>

      {/* ── Warm sky background ── */}
      <rect width="900" height="700" fill="url(#lgSkyGrad)" />

      {/* ── Ground haze at bottom ── */}
      <rect x="0" y="500" width="900" height="200" fill="url(#lgHaze)" />

      {/* ── Graph paper grid ── */}
      <g stroke="rgba(70,110,160,0.18)" strokeWidth="0.9">
        <line x1="0"   y1="50"  x2="900" y2="50"  />
        <line x1="0"   y1="100" x2="900" y2="100" />
        <line x1="0"   y1="150" x2="900" y2="150" />
        <line x1="0"   y1="200" x2="900" y2="200" />
        <line x1="0"   y1="250" x2="900" y2="250" />
        <line x1="0"   y1="300" x2="900" y2="300" />
        <line x1="0"   y1="350" x2="900" y2="350" />
        <line x1="0"   y1="400" x2="900" y2="400" />
        <line x1="0"   y1="450" x2="900" y2="450" />
        <line x1="0"   y1="500" x2="900" y2="500" />
        <line x1="0"   y1="550" x2="900" y2="550" />
        <line x1="0"   y1="600" x2="900" y2="600" />
        <line x1="0"   y1="650" x2="900" y2="650" />
        <line x1="50"  y1="0" x2="50"  y2="700" />
        <line x1="100" y1="0" x2="100" y2="700" />
        <line x1="150" y1="0" x2="150" y2="700" />
        <line x1="200" y1="0" x2="200" y2="700" />
        <line x1="250" y1="0" x2="250" y2="700" />
        <line x1="300" y1="0" x2="300" y2="700" />
        <line x1="350" y1="0" x2="350" y2="700" />
        <line x1="400" y1="0" x2="400" y2="700" />
        <line x1="450" y1="0" x2="450" y2="700" />
        <line x1="500" y1="0" x2="500" y2="700" />
        <line x1="550" y1="0" x2="550" y2="700" />
        <line x1="600" y1="0" x2="600" y2="700" />
        <line x1="650" y1="0" x2="650" y2="700" />
        <line x1="700" y1="0" x2="700" y2="700" />
        <line x1="750" y1="0" x2="750" y2="700" />
        <line x1="800" y1="0" x2="800" y2="700" />
        <line x1="850" y1="0" x2="850" y2="700" />
      </g>

      {/* ── Power infrastructure silhouettes ── */}
      {/* Cooling tower 1 — large */}
      <path d="M85,700 L128,430 Q152,382 178,430 L222,700 Z" fill="rgba(60,48,28,0.32)" />
      {/* Cooling tower 2 — medium */}
      <path d="M210,700 L260,390 Q285,340 312,390 L362,700 Z" fill="rgba(60,48,28,0.30)" />
      {/* Chimney stack */}
      <rect x="375" y="470" width="18" height="230" rx="2" fill="rgba(55,42,22,0.28)" />
      {/* Chimney smoke (faint circles) */}
      <circle cx="384" cy="455" r="18" fill="rgba(200,200,200,0.14)" />
      <circle cx="380" cy="420" r="24" fill="rgba(200,200,200,0.09)" />
      <circle cx="376" cy="382" r="30" fill="rgba(200,200,200,0.06)" />
      {/* House silhouette */}
      <rect x="460" y="596" width="90" height="104" rx="2" fill="rgba(55,50,35,0.30)" />
      <polygon points="455,596 550,596 505,548" fill="rgba(45,40,28,0.35)" />
      {/* Solar panel on roof */}
      <rect x="467" y="558" width="76" height="36" rx="3"
            fill="rgba(14,100,210,0.42)" stroke="rgba(14,165,233,0.55)" strokeWidth="1"
            transform="rotate(-2,505,576)" />
      <line x1="484" y1="559" x2="482" y2="592" stroke="rgba(14,165,233,0.40)" strokeWidth="0.8" />
      <line x1="504" y1="557" x2="502" y2="590" stroke="rgba(14,165,233,0.40)" strokeWidth="0.8" />
      <line x1="524" y1="556" x2="522" y2="589" stroke="rgba(14,165,233,0.40)" strokeWidth="0.8" />
      <line x1="470" y1="570" x2="542" y2="567" stroke="rgba(14,165,233,0.40)" strokeWidth="0.8" />
      <line x1="469" y1="580" x2="541" y2="577" stroke="rgba(14,165,233,0.40)" strokeWidth="0.8" />
      {/* Smaller house */}
      <rect x="555" y="618" width="65" height="82" rx="2" fill="rgba(55,50,35,0.25)" />
      <polygon points="550,618 625,618 590,580" fill="rgba(45,40,28,0.28)" />

      {/* ── Bar chart — right quadrant ── */}
      <rect x="572" y="490" width="24" height="160" rx="3" fill="url(#lgBarGrad)" fillOpacity="0.72" />
      <rect x="604" y="440" width="24" height="210" rx="3" fill="url(#lgBarGrad)" fillOpacity="0.72" />
      <rect x="636" y="510" width="24" height="140" rx="3" fill="url(#lgBarGrad)" fillOpacity="0.72" />
      <rect x="668" y="390" width="24" height="260" rx="3" fill="url(#lgBarGrad)" fillOpacity="0.75" />
      <rect x="700" y="455" width="24" height="195" rx="3" fill="url(#lgBarGrad)" fillOpacity="0.72" />
      <rect x="732" y="410" width="24" height="240" rx="3" fill="url(#lgBarGrad)" fillOpacity="0.75" />
      <rect x="764" y="470" width="24" height="180" rx="3" fill="url(#lgBarGrad)" fillOpacity="0.72" />
      <rect x="796" y="370" width="24" height="280" rx="3" fill="url(#lgBarGrad)" fillOpacity="0.78" />
      <rect x="828" y="420" width="24" height="230" rx="3" fill="url(#lgBarGrad)" fillOpacity="0.72" />
      <rect x="860" y="390" width="24" height="260" rx="3" fill="url(#lgBarGrad)" fillOpacity="0.75" />

      {/* ── Teal line chart ── */}
      <polyline
        points="20,530 120,500 220,465 330,430 440,440 560,395 670,355 780,320 890,295"
        stroke="#06b6d4"
        strokeWidth="3"
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeOpacity="0.92"
      />

      {/* ── Yellow/neon line chart ── */}
      <polyline
        points="20,570 120,538 220,500 330,460 440,408 560,348 670,295 780,252 890,215"
        stroke="#facc15"
        strokeWidth="3.5"
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeOpacity="0.95"
      />

      {/* ── Teal line data point dots ── */}
      <circle className={styles.svgNode} style={{ animationDelay: '0.0s' }} cx="120" cy="500" r="6" />
      <circle className={styles.svgNode} style={{ animationDelay: '1.5s' }} cx="330" cy="430" r="6" />
      <circle className={styles.svgNode} style={{ animationDelay: '3.0s' }} cx="560" cy="395" r="6" />
      <circle className={styles.svgNode} style={{ animationDelay: '4.5s' }} cx="780" cy="320" r="6" />

      {/* ── Yellow line data point dots ── */}
      <circle className={styles.svgNodeAccent} style={{ animationDelay: '0.8s' }} cx="120" cy="538" r="7" />
      <circle className={styles.svgNodeAccent} style={{ animationDelay: '2.3s' }} cx="330" cy="460" r="7" />
      <circle className={styles.svgNodeAccent} style={{ animationDelay: '3.8s' }} cx="560" cy="348" r="7" />
      <circle className={styles.svgNodeAccent} style={{ animationDelay: '5.3s' }} cx="780" cy="252" r="7" />

      {/* ── Extra accent dots — orange, purple, green ── */}
      <circle cx="440" cy="440" r="7" fill="#f97316" stroke="#fed7aa" strokeWidth="2" />
      <circle cx="440" cy="408" r="8" fill="#34d399" stroke="#d1fae5" strokeWidth="2.5" />
      <circle cx="670" cy="295" r="7" fill="#a78bfa" stroke="#ede9fe" strokeWidth="2" />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// LoginPage
// ---------------------------------------------------------------------------

type LoginTab = 'personal' | 'organization';

export default function LoginPage(): JSX.Element {
  const [tab, setTab] = useState<LoginTab>('personal');

  // Personal account fields
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // Organization fields
  const [orgSlug, setOrgSlug] = useState('');
  const [orgUsername, setOrgUsername] = useState('');
  const [orgPassword, setOrgPassword] = useState('');
  const [showOrgPassword, setShowOrgPassword] = useState(false);

  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();

  const from = (location.state as { from?: Location })?.from?.pathname ?? '/dashboard';

  const handlePersonalSubmit = async (event: React.FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    if (!username || !password) {
      setError('Please enter your username and password.');
      return;
    }
    setError(null);
    setLoading(true);
    try {
      const res = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password }),
      });
      const data: Record<string, unknown> = await res.json();
      if (!res.ok) {
        setError((data.detail as string | undefined) ?? 'Sign-in failed.');
        return;
      }
      login(data.access_token as string, {
        username,
        accountType: 'public',
        subscription: (data.subscription as string | undefined) ?? 'basic',
      });
      navigate(from, { replace: true });
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleOrgSubmit = async (event: React.FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    if (!orgSlug || !orgUsername || !orgPassword) {
      setError('Please fill in all fields.');
      return;
    }
    setError(null);
    setLoading(true);
    try {
      const res = await fetch(`/api/auth/org/${encodeURIComponent(orgSlug.trim())}/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: orgUsername, password: orgPassword }),
      });
      const data: Record<string, unknown> = await res.json();
      if (!res.ok) {
        // Surface the membership error verbatim
        setError((data.detail as string | undefined) ?? 'Sign-in failed.');
        return;
      }
      login(data.access_token as string, {
        username: orgUsername,
        accountType: 'organization',
        orgSlug: data.org_slug as string,
        orgName: data.org_name as string,
        orgRole: data.role as 'admin' | 'member',
      });
      navigate(from, { replace: true });
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const clearError = (): void => { if (error) setError(null); };

  return (
    <div className={styles.page}>
      {/* ================================================================
          Left panel — dark, geographic grid art
          ================================================================ */}
      <div className={styles.leftPanel} aria-hidden="true">
        <GridArtSVG />

        <div className={styles.leftPanelContent}>
          {/* Brand */}
          <div className={styles.brandRow}>
            <MangoGridIcon size={48} />
            <span className={styles.wordmark}>MangoGridStudio</span>
          </div>

          {/* Tagline */}
          <p className={styles.tagline}>
            Power Grid Optimization &amp; Analysis Platform
          </p>

          {/* Feature bullets */}
          <ul className={styles.featureList}>
            <li className={styles.featureItem}>
              <span className={styles.featureDot} />
              Real-time constraint mapping
            </li>
            <li className={styles.featureItem}>
              <span className={styles.featureDot} />
              Unit commitment optimization
            </li>
            <li className={styles.featureItem}>
              <span className={styles.featureDot} />
              ISO market intelligence
            </li>
          </ul>
        </div>
      </div>

      {/* ================================================================
          Right panel — white form panel
          ================================================================ */}
      <div className={styles.rightPanel}>
        {/* Mobile-only header (left panel is hidden on mobile) */}
        <div className={styles.mobileHeader}>
          <MangoGridIcon size={32} />
          <span
            style={{
              fontSize: '16px',
              fontWeight: 600,
              color: '#0f172a',
            }}
          >
            MangoGridStudio
          </span>
        </div>
        <p className={styles.mobileSubtext}>Network Operations Platform</p>

        {/* Form card */}
        <div className={styles.formCard}>
          {/* Form brand row */}
          <div className={styles.formBrandRow}>
            <MangoGridIcon size={32} />
            <span className={styles.formWordmark}>MangoGridStudio</span>
          </div>

          {/* Account type tabs */}
          <div className={styles.tabBar} role="tablist" aria-label="Account type">
            <button
              role="tab"
              aria-selected={tab === 'personal'}
              className={tab === 'personal' ? styles.tabActive : styles.tab}
              onClick={() => { setTab('personal'); setError(null); }}
            >
              Personal
            </button>
            <button
              role="tab"
              aria-selected={tab === 'organization'}
              className={tab === 'organization' ? styles.tabActive : styles.tab}
              onClick={() => { setTab('organization'); setError(null); }}
            >
              Organization
            </button>
          </div>

          {/* ── Personal sign-in ── */}
          {tab === 'personal' && (
            <>
              <h1 className={styles.formHeading}>Sign in to your account</h1>
              <p className={styles.formSubheading}>Free public account · mangogrid.ai</p>

              <form
                data-testid="login-form"
                onSubmit={(e) => void handlePersonalSubmit(e)}
                aria-label="Personal sign in form"
                noValidate
              >
                <div className={styles.fieldGroup}>
                  <div className={styles.field}>
                    <label htmlFor="login-username" className={styles.label}>
                      Email or Username
                    </label>
                    <input
                      id="login-username"
                      name="username"
                      type="text"
                      className={styles.input}
                      value={username}
                      onChange={(e) => { setUsername(e.target.value); clearError(); }}
                      autoComplete="username"
                      autoFocus
                      placeholder="you@example.com"
                    />
                  </div>

                  <div className={styles.field}>
                    <label htmlFor="login-password" className={styles.label}>
                      Password
                    </label>
                    <div className={styles.passwordWrapper}>
                      <input
                        id="login-password"
                        name="password"
                        type={showPassword ? 'text' : 'password'}
                        className={styles.input}
                        value={password}
                        onChange={(e) => { setPassword(e.target.value); clearError(); }}
                        autoComplete="current-password"
                        placeholder="••••••••"
                      />
                      <button
                        type="button"
                        className={styles.eyeButton}
                        onClick={() => setShowPassword((p) => !p)}
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                      >
                        {showPassword ? <EyeClosedIcon /> : <EyeOpenIcon />}
                      </button>
                    </div>
                  </div>
                </div>

                <div className={styles.forgotRow}>
                  <button
                    type="button"
                    className={styles.forgotLink}
                    onClick={() => { alert('Password reset coming soon.'); }}
                  >
                    Forgot password?
                  </button>
                </div>

                {error !== null && (
                  <p className={styles.errorMessage} role="alert">{error}</p>
                )}

                <button type="submit" className={styles.btnPrimary} disabled={loading}>
                  {loading ? 'Signing in…' : 'Sign In'}
                </button>

                <div className={styles.divider} aria-hidden="true">
                  <div className={styles.dividerLine} />
                  <span className={styles.dividerText}>or</span>
                  <div className={styles.dividerLine} />
                </div>

                <button
                  type="button"
                  className={styles.btnSecondary}
                  onClick={() => { navigate('/create-account'); }}
                >
                  Create a free account
                </button>
              </form>
            </>
          )}

          {/* ── Organization sign-in ── */}
          {tab === 'organization' && (
            <>
              <h1 className={styles.formHeading}>Sign in with Organization</h1>
              <p className={styles.formSubheading}>
                Access your private organization workspace.
                <br />
                <span className={styles.orgNote}>
                  You must be invited by an admin to join an organization.
                </span>
              </p>

              <form
                data-testid="org-login-form"
                onSubmit={(e) => void handleOrgSubmit(e)}
                aria-label="Organization sign in form"
                noValidate
              >
                <div className={styles.fieldGroup}>
                  <div className={styles.field}>
                    <label htmlFor="org-slug" className={styles.label}>
                      Organization Slug
                    </label>
                    <div className={styles.orgSlugWrapper}>
                      <span className={styles.orgSlugPrefix}>mangogrid.ai/org/</span>
                      <input
                        id="org-slug"
                        name="org_slug"
                        type="text"
                        className={styles.orgSlugInput}
                        value={orgSlug}
                        onChange={(e) => { setOrgSlug(e.target.value); clearError(); }}
                        placeholder="acme-energy"
                        autoComplete="off"
                        autoFocus
                      />
                    </div>
                  </div>

                  <div className={styles.field}>
                    <label htmlFor="org-username" className={styles.label}>
                      Username
                    </label>
                    <input
                      id="org-username"
                      name="username"
                      type="text"
                      className={styles.input}
                      value={orgUsername}
                      onChange={(e) => { setOrgUsername(e.target.value); clearError(); }}
                      autoComplete="username"
                      placeholder="your-username"
                    />
                  </div>

                  <div className={styles.field}>
                    <label htmlFor="org-password" className={styles.label}>
                      Password
                    </label>
                    <div className={styles.passwordWrapper}>
                      <input
                        id="org-password"
                        name="password"
                        type={showOrgPassword ? 'text' : 'password'}
                        className={styles.input}
                        value={orgPassword}
                        onChange={(e) => { setOrgPassword(e.target.value); clearError(); }}
                        autoComplete="current-password"
                        placeholder="••••••••"
                      />
                      <button
                        type="button"
                        className={styles.eyeButton}
                        onClick={() => setShowOrgPassword((p) => !p)}
                        aria-label={showOrgPassword ? 'Hide password' : 'Show password'}
                      >
                        {showOrgPassword ? <EyeClosedIcon /> : <EyeOpenIcon />}
                      </button>
                    </div>
                  </div>
                </div>

                {error !== null && (
                  <p className={styles.errorMessage} role="alert">{error}</p>
                )}

                <button type="submit" className={styles.btnPrimary} disabled={loading}>
                  {loading ? 'Checking membership…' : 'Sign In to Organization'}
                </button>

                <p className={styles.orgHint}>
                  Don't have an account yet?{' '}
                  <span>Contact your organization admin to receive an invitation.</span>
                </p>
              </form>
            </>
          )}

          {/* Footer */}
          <footer className={styles.footer}>
            <span>© 2026 MangoGridStudio</span>
            <span className={styles.footerSep}>·</span>
            <a href="#privacy" className={styles.footerLink}>Privacy</a>
            <span className={styles.footerSep}>·</span>
            <a href="#terms" className={styles.footerLink}>Terms</a>
          </footer>
        </div>
      </div>
    </div>
  );
}
